/**
 * The parts a build is made of, and how much power each one draws at most.
 *
 * Processor and graphics card figures are the manufacturers' own ratings, so a
 * visitor can check any of them against a spec sheet. Everything else is a
 * generic part type with a typical maximum: a stick of DDR5 draws about the same
 * whoever made it, and listing brands there would add choices without accuracy.
 *
 * Model names are data, not prose — "Ryzen 7 9800X3D" is the same in every
 * language — so they live here rather than in a translation catalog. Generic part
 * types are translated, through the `part.*` keys.
 *
 * IDs are written into share links. Never rename or remove one; a link holding an
 * unknown id restores nothing.
 */

export type Brand = 'amd' | 'intel' | 'nvidia'

export interface PartModel {
  /** Stable id, used in share links. */
  readonly id: string
  readonly brand: Brand
  /** Product name without the brand, as the manufacturer writes it. */
  readonly name: string
  /** Maximum sustained draw at stock settings, in watts. */
  readonly watts: number
}

/**
 * Desktop processors, newest generation first within each brand.
 *
 * The rating is the package power a CPU may sustain at stock settings, which is
 * what the power supply must deliver — not the "TDP" on the box, which is a
 * cooling figure and can be less than half of it:
 *  - Intel: Maximum Turbo Power (PL2), from ark.intel.com.
 *  - AMD: Package Power Tracking (PPT), 1.35 × TDP on AM4 and AM5.
 */
export const CPUS: readonly PartModel[] = [
  { id: 'ryzen-9-9950x3d', brand: 'amd', name: 'Ryzen 9 9950X3D', watts: 230 },
  { id: 'ryzen-9-9950x', brand: 'amd', name: 'Ryzen 9 9950X', watts: 230 },
  { id: 'ryzen-9-9900x', brand: 'amd', name: 'Ryzen 9 9900X', watts: 162 },
  { id: 'ryzen-7-9800x3d', brand: 'amd', name: 'Ryzen 7 9800X3D', watts: 162 },
  { id: 'ryzen-7-9700x', brand: 'amd', name: 'Ryzen 7 9700X', watts: 88 },
  { id: 'ryzen-5-9600x', brand: 'amd', name: 'Ryzen 5 9600X', watts: 88 },
  { id: 'ryzen-9-7950x', brand: 'amd', name: 'Ryzen 9 7950X', watts: 230 },
  { id: 'ryzen-9-7900x', brand: 'amd', name: 'Ryzen 9 7900X', watts: 230 },
  { id: 'ryzen-7-7800x3d', brand: 'amd', name: 'Ryzen 7 7800X3D', watts: 162 },
  { id: 'ryzen-7-7700x', brand: 'amd', name: 'Ryzen 7 7700X', watts: 142 },
  { id: 'ryzen-7-7700', brand: 'amd', name: 'Ryzen 7 7700', watts: 88 },
  { id: 'ryzen-5-7600x', brand: 'amd', name: 'Ryzen 5 7600X', watts: 142 },
  { id: 'ryzen-5-7600', brand: 'amd', name: 'Ryzen 5 7600', watts: 88 },
  { id: 'ryzen-9-5950x', brand: 'amd', name: 'Ryzen 9 5950X', watts: 142 },
  { id: 'ryzen-9-5900x', brand: 'amd', name: 'Ryzen 9 5900X', watts: 142 },
  { id: 'ryzen-7-5800x3d', brand: 'amd', name: 'Ryzen 7 5800X3D', watts: 142 },
  { id: 'ryzen-7-5800x', brand: 'amd', name: 'Ryzen 7 5800X', watts: 142 },
  { id: 'ryzen-7-5700x', brand: 'amd', name: 'Ryzen 7 5700X', watts: 88 },
  { id: 'ryzen-5-5600x', brand: 'amd', name: 'Ryzen 5 5600X', watts: 88 },
  { id: 'ryzen-5-5600', brand: 'amd', name: 'Ryzen 5 5600', watts: 88 },
  { id: 'core-ultra-9-285k', brand: 'intel', name: 'Core Ultra 9 285K', watts: 250 },
  { id: 'core-ultra-7-265k', brand: 'intel', name: 'Core Ultra 7 265K', watts: 250 },
  { id: 'core-ultra-5-245k', brand: 'intel', name: 'Core Ultra 5 245K', watts: 159 },
  { id: 'core-i9-14900k', brand: 'intel', name: 'Core i9-14900K', watts: 253 },
  { id: 'core-i7-14700k', brand: 'intel', name: 'Core i7-14700K', watts: 253 },
  { id: 'core-i5-14600k', brand: 'intel', name: 'Core i5-14600K', watts: 181 },
  { id: 'core-i5-14400', brand: 'intel', name: 'Core i5-14400', watts: 148 },
  { id: 'core-i3-14100', brand: 'intel', name: 'Core i3-14100', watts: 110 },
  { id: 'core-i9-13900k', brand: 'intel', name: 'Core i9-13900K', watts: 253 },
  { id: 'core-i7-13700k', brand: 'intel', name: 'Core i7-13700K', watts: 253 },
  { id: 'core-i5-13600k', brand: 'intel', name: 'Core i5-13600K', watts: 181 },
  { id: 'core-i5-13400', brand: 'intel', name: 'Core i5-13400', watts: 148 },
  { id: 'core-i9-12900k', brand: 'intel', name: 'Core i9-12900K', watts: 241 },
  { id: 'core-i7-12700k', brand: 'intel', name: 'Core i7-12700K', watts: 190 },
  { id: 'core-i5-12600k', brand: 'intel', name: 'Core i5-12600K', watts: 150 },
  { id: 'core-i5-12400', brand: 'intel', name: 'Core i5-12400', watts: 117 },
  { id: 'core-i3-12100', brand: 'intel', name: 'Core i3-12100', watts: 89 },
]

/**
 * Desktop graphics cards at their reference board power — NVIDIA's "Total
 * Graphics Power", AMD's "Total Board Power", Intel's "Total Board Power".
 * Factory-overclocked partner cards can draw more; the custom option covers them.
 */
export const GPUS: readonly PartModel[] = [
  { id: 'rtx-5090', brand: 'nvidia', name: 'GeForce RTX 5090', watts: 575 },
  { id: 'rtx-5080', brand: 'nvidia', name: 'GeForce RTX 5080', watts: 360 },
  { id: 'rtx-5070-ti', brand: 'nvidia', name: 'GeForce RTX 5070 Ti', watts: 300 },
  { id: 'rtx-5070', brand: 'nvidia', name: 'GeForce RTX 5070', watts: 250 },
  { id: 'rtx-5060-ti', brand: 'nvidia', name: 'GeForce RTX 5060 Ti', watts: 180 },
  { id: 'rtx-5060', brand: 'nvidia', name: 'GeForce RTX 5060', watts: 145 },
  { id: 'rtx-4090', brand: 'nvidia', name: 'GeForce RTX 4090', watts: 450 },
  { id: 'rtx-4080-super', brand: 'nvidia', name: 'GeForce RTX 4080 SUPER', watts: 320 },
  { id: 'rtx-4080', brand: 'nvidia', name: 'GeForce RTX 4080', watts: 320 },
  { id: 'rtx-4070-ti-super', brand: 'nvidia', name: 'GeForce RTX 4070 Ti SUPER', watts: 285 },
  { id: 'rtx-4070-ti', brand: 'nvidia', name: 'GeForce RTX 4070 Ti', watts: 285 },
  { id: 'rtx-4070-super', brand: 'nvidia', name: 'GeForce RTX 4070 SUPER', watts: 220 },
  { id: 'rtx-4070', brand: 'nvidia', name: 'GeForce RTX 4070', watts: 200 },
  { id: 'rtx-4060-ti', brand: 'nvidia', name: 'GeForce RTX 4060 Ti', watts: 160 },
  { id: 'rtx-4060', brand: 'nvidia', name: 'GeForce RTX 4060', watts: 115 },
  { id: 'rtx-3090-ti', brand: 'nvidia', name: 'GeForce RTX 3090 Ti', watts: 450 },
  { id: 'rtx-3090', brand: 'nvidia', name: 'GeForce RTX 3090', watts: 350 },
  { id: 'rtx-3080-ti', brand: 'nvidia', name: 'GeForce RTX 3080 Ti', watts: 350 },
  { id: 'rtx-3080', brand: 'nvidia', name: 'GeForce RTX 3080', watts: 320 },
  { id: 'rtx-3070-ti', brand: 'nvidia', name: 'GeForce RTX 3070 Ti', watts: 290 },
  { id: 'rtx-3070', brand: 'nvidia', name: 'GeForce RTX 3070', watts: 220 },
  { id: 'rtx-3060-ti', brand: 'nvidia', name: 'GeForce RTX 3060 Ti', watts: 200 },
  { id: 'rtx-3060', brand: 'nvidia', name: 'GeForce RTX 3060', watts: 170 },
  { id: 'rtx-3050', brand: 'nvidia', name: 'GeForce RTX 3050', watts: 130 },
  { id: 'rx-9070-xt', brand: 'amd', name: 'Radeon RX 9070 XT', watts: 304 },
  { id: 'rx-9070', brand: 'amd', name: 'Radeon RX 9070', watts: 220 },
  { id: 'rx-9060-xt', brand: 'amd', name: 'Radeon RX 9060 XT', watts: 160 },
  { id: 'rx-7900-xtx', brand: 'amd', name: 'Radeon RX 7900 XTX', watts: 355 },
  { id: 'rx-7900-xt', brand: 'amd', name: 'Radeon RX 7900 XT', watts: 315 },
  { id: 'rx-7900-gre', brand: 'amd', name: 'Radeon RX 7900 GRE', watts: 260 },
  { id: 'rx-7800-xt', brand: 'amd', name: 'Radeon RX 7800 XT', watts: 263 },
  { id: 'rx-7700-xt', brand: 'amd', name: 'Radeon RX 7700 XT', watts: 245 },
  { id: 'rx-7600-xt', brand: 'amd', name: 'Radeon RX 7600 XT', watts: 190 },
  { id: 'rx-7600', brand: 'amd', name: 'Radeon RX 7600', watts: 165 },
  { id: 'rx-6950-xt', brand: 'amd', name: 'Radeon RX 6950 XT', watts: 335 },
  { id: 'rx-6900-xt', brand: 'amd', name: 'Radeon RX 6900 XT', watts: 300 },
  { id: 'rx-6800-xt', brand: 'amd', name: 'Radeon RX 6800 XT', watts: 300 },
  { id: 'rx-6800', brand: 'amd', name: 'Radeon RX 6800', watts: 250 },
  { id: 'rx-6750-xt', brand: 'amd', name: 'Radeon RX 6750 XT', watts: 250 },
  { id: 'rx-6700-xt', brand: 'amd', name: 'Radeon RX 6700 XT', watts: 230 },
  { id: 'rx-6650-xt', brand: 'amd', name: 'Radeon RX 6650 XT', watts: 180 },
  { id: 'rx-6600-xt', brand: 'amd', name: 'Radeon RX 6600 XT', watts: 160 },
  { id: 'rx-6600', brand: 'amd', name: 'Radeon RX 6600', watts: 132 },
  { id: 'arc-b580', brand: 'intel', name: 'Arc B580', watts: 190 },
  { id: 'arc-b570', brand: 'intel', name: 'Arc B570', watts: 150 },
  { id: 'arc-a770', brand: 'intel', name: 'Arc A770', watts: 225 },
  { id: 'arc-a750', brand: 'intel', name: 'Arc A750', watts: 225 },
  { id: 'arc-a580', brand: 'intel', name: 'Arc A580', watts: 185 },
  { id: 'arc-a380', brand: 'intel', name: 'Arc A380', watts: 75 },
]

/** Brand names are trademarks, written the same in every language. */
export const BRAND_NAMES: Readonly<Record<Brand, string>> = {
  amd: 'AMD',
  intel: 'Intel',
  nvidia: 'NVIDIA',
}

/** Brands in the order a catalogue lists them, for grouping a picker. */
export function brandsOf(models: readonly PartModel[]): Brand[] {
  return [...new Set(models.map((model) => model.brand))]
}

export function findModel(models: readonly PartModel[], id: string): PartModel | undefined {
  return models.find((model) => model.id === id)
}

/**
 * What the board itself draws: chipset, VRM losses, on-board audio and network,
 * and the USB keyboard and mouse hanging off it. Typical maxima — a board's own
 * consumption is rarely published.
 */
export const PLATFORM_WATTS = {
  desktop: 50,
  'mini-itx': 30,
  // Workstation and server boards add a management controller, more lanes and NICs.
  server: 80,
} as const

export type Platform = keyof typeof PLATFORM_WATTS
export const PLATFORMS = Object.keys(PLATFORM_WATTS) as Platform[]

/** Per module. DDR5 moves voltage regulation onto the stick, so it draws more. */
export const MEMORY_WATTS = {
  ddr5: 5,
  ddr4: 4,
  ddr3: 4,
} as const

export type MemoryType = keyof typeof MEMORY_WATTS
export const MEMORY_TYPES = Object.keys(MEMORY_WATTS) as MemoryType[]

/** Per drive, under load. A hard drive's spin-up surge is covered by the headroom. */
export const STORAGE_WATTS = {
  'nvme-gen5': 12,
  nvme: 8,
  'sata-ssd': 4,
  'hdd-35': 10,
  'hdd-25': 3,
} as const

export type StorageType = keyof typeof STORAGE_WATTS
export const STORAGE_TYPES = Object.keys(STORAGE_WATTS) as StorageType[]

/**
 * A DVD or Blu-ray drive while writing. No longer a picker of its own — it goes
 * under "other components" — but share links from before that still carry a
 * count of them (`odd=`), and the field's help quotes it as an example.
 */
export const OPTICAL_DRIVE_WATTS = 20

/** A 120 or 140 mm case fan at full speed, with allowance for RGB lighting. */
export const FAN_WATTS = 4

/**
 * Typical on-mode draw of an LED-backlit monitor by size. Monitors plug into the
 * wall, not the power supply, so they count toward energy cost but never toward
 * the PSU size.
 */
export const MONITOR_WATTS = {
  small: 20,
  medium: 30,
  large: 45,
} as const

export type MonitorSize = keyof typeof MONITOR_WATTS
export const MONITOR_SIZES = Object.keys(MONITOR_WATTS) as MonitorSize[]

/** Narrows a share-link token to one of a table's keys. */
export function isKeyOf<T extends object>(table: T, value: unknown): value is keyof T {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(table, value)
}
